import React, { useState } from "react";
import { Typography } from "./typography";
import StackFormLayout from "./stack-form-layout";
import StackFormButtons from "./stack-form-buttons";

interface TransactionDetail {
  label: string;
  value: string;
}

interface ConfirmStepProps {
  title: string;
  formattedAmount: string;
  transactionDetails: TransactionDetail[];
  description: string;
  buttonText: string;
  onStepComplete: () => void | Promise<void>;
  onStepBack: () => void;
  onCancel: () => void;
  loading?: boolean;
}

const ConfirmStep: React.FC<ConfirmStepProps> = ({
  title,
  formattedAmount,
  transactionDetails,
  description,
  buttonText,
  onStepComplete,
  onStepBack,
  onCancel,
  loading: externalLoading = false,
}) => {
  const [internalLoading, setInternalLoading] = useState(false);

  const isLoading = externalLoading || internalLoading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setInternalLoading(true);
      await onStepComplete();
    } catch (error) {
      console.error("Error in onStepComplete:", error);
    } finally {
      setInternalLoading(false);
    }
  };

  return (
    <StackFormLayout
      title={title}
      onCancel={onCancel}
      showCancelHeader={true}
      maxWidth="max-w-2xl"
    >
      <div className="bg-gray-100 rounded-lg p-4 text-center mb-8">
        <Typography variant="h1" className="text-4xl font-bold text-gray-900">
          {formattedAmount}
        </Typography>
      </div>

      <div className="space-y-2 mb-2">
        <Typography variant="p" className="text-gray-600 font-semibold">
          Transaction details
        </Typography>

        <div className="space-y-4 py-4 border border-gray-200 rounded-lg">
          {transactionDetails.map((item, index, array) => (
            <div
              key={item.label}
              className={`flex justify-between items-center px-4 ${
                index < array.length - 1 ? "border-b border-gray-200 pb-4" : ""
              }`}
            >
              <Typography
                variant="p"
                className="text-sm md:text-base text-gray-600"
              >
                {item.label}
              </Typography>
              <Typography
                variant="p"
                className="font-semibold text-sm md:text-base text-gray-900"
              >
                {item.value}
              </Typography>
            </div>
          ))}
        </div>
      </div>

      <div className="text-center my-4 md:my-8 px-2 md:px-32">
        <Typography variant="p" className="text-gray-500 text-sm">
          {description}
        </Typography>
      </div>

      <form onSubmit={handleSubmit}>
        <StackFormButtons
          onBack={onStepBack}
          isSubmitType={true}
          continueText={buttonText}
          loading={isLoading}
        />
      </form>
    </StackFormLayout>
  );
};

export default ConfirmStep;
